import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IArticleOptionGroup, IArticleProductionSiteAllocation, ITableViewArticle } from "@/data/model/Article";
import type { IOfferCategory } from "@/data/model/OfferCategory";
import type { IOffer } from "@/data/model/Offer";
import { Format } from "@/data/util/Format";

import { translate } from "@/shared/localization/language";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";
import { PortalFormat } from "@/portal/util/PortalFormat";

export class ArticleTableController {
    private $element: JQuery;
    private dao: CatalogDao;
    private dataTable: DataTable<ITableViewArticle>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<CatalogDao>(CatalogDao);

        const tableConfig: IDataTableConfig<ITableViewArticle> = {
            columnMapping: [
                {
                    name: i18n("general.fields.designation"),
                    property: "internalName",
                    styles: { width: "14%" },
                    formatter: (m) => {
                        if (m.internalName !== undefined) return Format.htmlEscape(m.internalName);

                        return Format.htmlEscape(translate(m.name));
                    },
                },
                {
                    name: i18n("general.entities.offers"),
                    property: "offers",
                    styles: { width: "14%" },
                    formatter: (m, p) => {
                        return PortalFormat.toCommaSeparatedList<IOffer>(
                            p,
                            (c) => Format.htmlEscape(c.name)!,
                            (c) => "/offer/" + c.id,
                            4
                        );
                    },
                },
                {
                    name: i18n("item.fields.categories"),
                    property: "offerCategories",
                    styles: { width: "14%" },
                    formatter: (m, p) => {
                        return PortalFormat.toCommaSeparatedList<IOfferCategory>(
                            p,
                            (c) => Format.htmlEscape(translate(c.name))!,
                            (c) => "/offercategory/" + c.id,
                            4
                        );
                    },
                },
                {
                    name: i18n("general.entities.optionGroups"),
                    property: "optionGroups",
                    styles: { width: "14%" },
                    formatter: (m, p) => {
                        return PortalFormat.toCommaSeparatedList<IArticleOptionGroup>(
                            p,
                            (c) => {
                                if (c.internalName !== undefined) return Format.htmlEscape(c.internalName)!;

                                return Format.htmlEscape(translate(c.name))!;
                            },
                            (c) => "/optiongroup/" + c.id,
                            4
                        );
                    },
                },
                {
                    name: i18n("general.fields.price"),
                    property: "price",
                    styles: { width: "7%" },
                    formatter: (m, p) => {
                        if (m.isOpenPriceEnabled) {
                            return i18n("general.fields.open");
                        } else {
                            return p.toFixed(2);
                        }
                    },
                },
                {
                    name: i18n("general.fields.internalNote"),
                    property: "internalNote",
                    styles: { width: "10%" },
                },
                {
                    name: i18n("general.entities.productionSites"),
                    property: "productionSites",
                    styles: { width: "14%" },
                    formatter: (m) => {
                        return PortalFormat.toCommaSeparatedList<IArticleProductionSiteAllocation>(
                            m.productionSites,
                            (c) => Format.htmlEscape(c.productionSite.name)!,
                            (c) => "/productionsite/" + c.productionSite.id,
                            4
                        );
                    },
                },
                {
                    name: i18n("general.fields.available"),
                    property: "isAvailable",
                    formatter: (m, p) => (p ? i18n("general.yes") : i18n("general.no")),
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: true,
            locationDependend: true,
            defaultSortProperty: "internalName",
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.dataTable = new DataTable<ITableViewArticle>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao.articleDataSource,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                location.href = "/article/" + m.id;
            },
            (m) => {
                window.open("/article/" + m.id);
            }
        );

        this.dataTable.listenTo(this.search);
        portalEvents.OnLocationChanged.add(() => {
            this.dataTable.loadData(true);
        }, this);
    }
}
